import type {ReactElement} from 'react';
import React from 'react';
import {Card} from '@Components/event-wizard/card';
import {ClickableDiv} from '@Components/clickable-div';
import {useEventWizardOnlineVenue, useEventWizardShouldCheckErrState, useEventWizardVenue} from '@Components/event-wizard/event-wizard.hooks';
import {EventVenueType, getIsOnlineVenueComplete, isEventVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {getAssetUrl} from '@Utils/s3.util';
import {isGoogleMeetLink, isValidURL2, isZoomLink} from '@Utils/url.util';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import styles from './event-wizard-venue-summary.module.scss';
import type {EventWizardVenueSummaryProps} from './event-wizard-venue-summary.types';

export function EventWizardVenueSummary({toggleExpandedState, isExpanded}: EventWizardVenueSummaryProps): ReactElement {
  const venue = useEventWizardVenue();
  const onlineVenue = useEventWizardOnlineVenue();
  const shouldCheckForErr = useEventWizardShouldCheckErrState();

  const isInErrorState = (): boolean => {
    if (venue.selectedVenueType === EventVenueType.ONLINE && !getIsOnlineVenueComplete(onlineVenue)) {
      return true;
    }
    return shouldCheckForErr && !isEventVenueComplete(venue);
  };

  const getTitleAndSubtitles = (): [string, string | undefined, string | undefined] => {
    let title: string = window.i18next.t('pmwjs_location');
    let subtitle: string | undefined = window.i18next.t('pmwjs_set_location_or_online_link');
    let subtitle2: string | undefined = undefined;

    if (venue.selectedVenueType === EventVenueType.OFFLINE && venue.offlineVenue) {
      title = venue.offlineVenue.name ?? window.i18next.t('pmwjs_location');
      subtitle = venue.offlineVenue.address;

      if (venue.offlineVenue?.addressDirections) {
        subtitle2 = venue.offlineVenue.addressDirections;
      }
    } else if (venue.selectedVenueType === EventVenueType.ONLINE && venue.onlineVenueURL) {
      title = window.i18next.t('pmwjs_online');
      subtitle = !isValidURL2(venue.onlineVenueURL) ? venue.onlineVenueURL : undefined;
    }
    return [title, subtitle, subtitle2];
  };

  const getThumbnailIcon = (): ReactElement | undefined => {
    let iconEl: ReactElement | undefined = <Icon className="content-disabled spacing-m-3" icon="icon-map-pin" size={IconSize.SIZE_ICON_16} />;

    if (venue.selectedVenueType === EventVenueType.OFFLINE && venue.offlineVenue) {
      iconEl = <Icon className="content-primary spacing-m-3" icon="icon-map-pin" size={IconSize.SIZE_ICON_16} />;
    } else if (venue.selectedVenueType === EventVenueType.ONLINE && venue.onlineVenueURL) {
      iconEl = getOnlineVenueIcon();
    }
    return iconEl;
  };

  const getOnlineVenueIcon = (): ReactElement | undefined => {
    if (!venue.onlineVenueURL) {
      return undefined;
    }
    let iconEl: ReactElement | undefined;

    if (isGoogleMeetLink(venue.onlineVenueURL)) {
      iconEl = (
        <div className={`${styles.onlineIconImgContainer} flex-row-align-center justify-content-center`}>
          <img className={styles.onlineIconImg} src={getAssetUrl('google-meet.svg')} alt="icon-google-meet" />
        </div>
      );
    } else if (isZoomLink(venue.onlineVenueURL)) {
      iconEl = (
        <div className={`${styles.onlineIconImgContainer} flex-row-align-center justify-content-center`}>
          <img className={styles.onlineIconImg} src={getAssetUrl('zoom-logo.svg')} alt="icon-zoom" />
        </div>
      );
    } else {
      iconEl = <Icon className="content-primary spacing-m-3" icon="icon-video" size={IconSize.SIZE_ICON_16} />;
    }
    return iconEl;
  };

  const [title, subtitle, subtitle2] = getTitleAndSubtitles();
  const iconEl: ReactElement | undefined = getThumbnailIcon();

  return (
    <div className={isExpanded ? '_hidden' : ''}>
      <ClickableDiv
        className={`${styles.summaryCard} ${isInErrorState() ? 'border-s-danger' : ''} flex-row-align-center spacing-p-2 border-s-standard radius-8 _cursor-pointer ${styles.fadeIn}`}
        onClick={toggleExpandedState}
      >
        <Card text={title} subtext={subtitle} subtext2={subtitle2} thumbnailIcon={iconEl} isThumbnailIconDisabled={!isEventVenueComplete(venue)} />
      </ClickableDiv>
      {isInErrorState() ? <EventWizardErrorInfo className="spacing-m-t-1" text={window.i18next.t('pmwjs_please_add_venue')} /> : undefined}
    </div>
  );
}
